import type { BoardDef, Roster } from '@archon3d/core';
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { COLORS } from '../scenes/board/constants';
import { canvasLayout, hexagonOutline, pointsAttribute } from './canvasLayout';
import type { Coord, Tool } from './editorStore';

interface Props {
  portraits?: Record<string, string>;
  arenaLabels?: Record<string, string>;
  def: BoardDef;
  roster: Roster;
  tool: Tool;
  /** The wall tool's first pick, drawn as chosen. */
  wallFrom: Coord | null;
  /** Whether a press goes on over the cells the pointer is dragged across; the paint tool's does. */
  drag?: boolean;
  /** A cell pressed, or with `stroke` one dragged onto after the press (editor:quality#12). */
  onCell: (x: number, y: number, stroke?: boolean) => void;
}

const CELL_FILL: Record<string, string> = {
  L: COLORS.light,
  D: COLORS.dark,
  '~': COLORS.cycling,
};
const TERRAIN: Record<string, string> = { L: 'light', D: 'dark', '~': 'cycling', '#': 'void' };
const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/** The cell under a pointer, as its `x,y`, or null off the board. */
function cellUnder(event: ReactPointerEvent): string | null {
  const element = document.elementFromPoint(event.clientX, event.clientY);
  return element?.closest('[data-cell]')?.getAttribute('data-cell') ?? null;
}

/**
 * Top-down picture of the board as SVG; every cell is marked with its coordinates. A press on a
 * cell edits it, and with the paint tool a drag paints every cell it crosses. From the keyboard
 * the board is one stop: the arrows move between cells and Enter or Space edits the one in focus
 * (editor:quality#7).
 */
export function BoardCanvas({
  def,
  roster,
  tool,
  wallFrom,
  drag = tool === 'paint',
  onCell,
  portraits = {},
  arenaLabels = {},
}: Props) {
  const svg = useRef<SVGSVGElement>(null);
  /** The cell the press under way edited last, while the button is held. */
  const pressed = useRef<string | null>(null);
  const [focus, setFocus] = useState({ x: 0, y: 0 });
  const focusX = Math.min(focus.x, def.width - 1),
    focusY = Math.min(focus.y, def.height - 1);
  const edit = (at: string, stroke?: boolean) => {
    const [x, y] = at.split(',').map(Number) as [number, number];
    onCell(x, y, stroke);
  };
  const layout = useMemo(() => canvasLayout(def), [def.grid, def.width, def.height]);
  const outline = useMemo(() => hexagonOutline(def), [def.grid, def.width, def.height, def.cells]);
  const size = 40;
  // Room for the hexagon outline, whose corners reach a sixth of a tile past the outermost hexes.
  const margin = size / 5;
  const factionColor = new Map(def.factions.map((f) => [f.id, f.color]));
  const powerPoints = new Set(def.powerPoints.map(([x, y]) => `${x},${y}`));
  const pieces = new Map(def.placements.map((p) => [`${p.x},${p.y}`, p]));
  /** What a cell holds, for a screen reader: its terrain, power point, piece and arena. */
  const describe = (x: number, y: number, ch: string) => {
    const piece = pieces.get(`${x},${y}`);
    return [
      `${x}, ${y}`,
      TERRAIN[ch] ?? ch,
      ...(powerPoints.has(`${x},${y}`) ? ['power point'] : []),
      ...(piece
        ? [
            `${roster[piece.character]?.name ?? piece.character} (${def.factions.find((f) => f.id === piece.faction)?.name ?? piece.faction})`,
          ]
        : []),
      arenaLabels[`${x},${y}`] ?? 'Default Arena',
    ].join(' · ');
  };
  const cells: { x: number; y: number; ch: string }[] = [];
  def.cells.forEach((row, y) => {
    for (let x = 0; x < def.width; x++) cells.push({ x, y, ch: row[x] ?? '#' });
  });

  return (
    <svg
      ref={svg}
      className={`editor-canvas tool-${tool}${drag ? ' drag' : ''}`}
      viewBox={`${-margin} ${-margin} ${layout.width + 2 * margin} ${layout.height + 2 * margin}`}
      data-testid="editor-canvas"
      role="group"
      aria-label={`${def.name}, ${def.width} by ${def.height}`}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        const at = (e.target as Element).closest('[data-cell]')?.getAttribute('data-cell');
        if (!at) return;
        pressed.current = at;
        edit(at);
      }}
      onPointerMove={(e) => {
        if (pressed.current === null) return;
        if (!drag || (e.buttons & 1) === 0) {
          pressed.current = null;
          return;
        }
        const at = cellUnder(e);
        if (!at || at === pressed.current) return;
        pressed.current = at;
        edit(at, true);
      }}
      onPointerUp={() => (pressed.current = null)}
      onPointerCancel={() => (pressed.current = null)}
    >
      {cells.map(({ x, y, ch }) => {
        const picked = wallFrom?.x === x && wallFrom?.y === y;
        return (
          <polygon
            key={`${x},${y}`}
            className={`cell${ch === '#' ? ' void' : ''}${picked ? ' picked' : ''}${arenaLabels[`${x},${y}`] ? ' has-arena' : ''}`}
            points={pointsAttribute(layout.outline(x, y))}
            fill={CELL_FILL[ch] ?? 'transparent'}
            data-cell={`${x},${y}`}
            role="button"
            aria-label={describe(x, y, ch)}
            tabIndex={x === focusX && y === focusY ? 0 : -1}
            onFocus={() => setFocus({ x, y })}
            // A press edits the cell as it goes down; a click with no press behind it (a screen
            // reader's, or a script's) edits it here.
            onClick={(e) => {
              if (e.detail === 0) onCell(x, y);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCell(x, y);
                return;
              }
              const step = ARROWS[e.key];
              if (!step) return;
              e.preventDefault();
              const nx = Math.max(0, Math.min(def.width - 1, x + step[0]));
              const ny = Math.max(0, Math.min(def.height - 1, y + step[1]));
              svg.current?.querySelector<SVGElement>(`[data-cell="${nx},${ny}"]`)?.focus();
            }}
          >
            <title>{`${x}, ${y} · ${arenaLabels[`${x},${y}`] ?? 'Default Arena'}`}</title>
          </polygon>
        );
      })}
      {outline && (
        <polygon
          className="board-outline"
          points={pointsAttribute(outline)}
          data-testid="board-outline"
        />
      )}
      {cells
        .filter((c) => powerPoints.has(`${c.x},${c.y}`))
        .map(({ x, y }) => {
          const c = layout.centre(x, y);
          return (
            <circle
              key={`pp-${x},${y}`}
              className="power-point"
              cx={c.x}
              cy={c.y}
              r={size * 0.43}
              stroke={COLORS.powerPoint}
            />
          );
        })}
      {(def.walls ?? []).map(([[ax, ay], [bx, by]]) => {
        const [p, q] = layout.edge({ x: ax, y: ay }, { x: bx, y: by });
        return (
          <line
            key={`wall-${ax},${ay}-${bx},${by}`}
            className="wall"
            x1={p.x}
            y1={p.y}
            x2={q.x}
            y2={q.y}
          />
        );
      })}
      {def.placements.map((p) => {
        const c = layout.centre(p.x, p.y);
        const color = factionColor.get(p.faction) ?? '#888888';
        const name = roster[p.character]?.name ?? p.character;
        return (
          <g key={`piece-${p.x},${p.y}`} className="piece">
            <circle cx={c.x} cy={c.y} r={size * 0.32} fill={color} />
            {portraits[p.character] ? (
              <>
                <image
                  href={portraits[p.character]}
                  x={c.x - 19}
                  y={c.y - 23}
                  width={38}
                  height={43}
                />
                {/* The side's ring round the portrait, which covers its disc (editor:quality#21). */}
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={size * 0.46}
                  fill="none"
                  stroke={color}
                  strokeWidth={3}
                />
              </>
            ) : (
              <text
                x={c.x}
                y={c.y}
                fill={isLight(color) ? '#1a1a1a' : '#ffffff'}
                fontSize={size * 0.3}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {abbreviate(name)}
              </text>
            )}
            <title>{`${name} (${def.factions.find((f) => f.id === p.faction)?.name ?? p.faction})`}</title>
          </g>
        );
      })}
    </svg>
  );
}

function abbreviate(name: string): string {
  return name.slice(0, 2).toUpperCase();
}

/** Whether black text reads better than white on a colour. */
function isLight(hex: string): boolean {
  const n = parseInt(hex.slice(1, 7), 16);
  if (Number.isNaN(n)) return false;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150;
}
